import React from 'react';

/**
 * Superfície branca do design system (at-55, frente A).
 *
 * Substitui a definição de card que hoje é recriada em quase todo `.css` de
 * tela, cada uma com o seu raio e a sua sombra.
 *
 * ⚠️ `interativo` NÃO é só estilo. Quando o card inteiro é clicável ele precisa
 * ser operável por teclado — Enter e Espaço —, e **Espaço não pode rolar a
 * página** (`preventDefault`). Isso é requisito da spec 01 §4 e foi uma correção
 * deliberada do projeto: antes só um botão "Acessar" dentro do card navegava, e
 * o resto era área morta de toque, o que fazia o consultor errar o alvo no
 * celular.
 */

interface PropsCard extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onClick'> {
  /** Torna o card inteiro clicável (mouse + teclado) e aplica hover/foco. */
  interativo?: boolean;
  onClick?: () => void;
  /** Rótulo para leitor de tela quando o conteúdo visual não basta. */
  rotuloAcessivel?: string;
  /**
   * `'card'` (padrão) aplica fundo, raio, borda e sombra.
   * `'nenhuma'` não aplica NADA disso — o consumidor define a superfície.
   *
   * ⚠️ Existe por uma limitação real do Tailwind: classes utilitárias têm todas
   * a mesma especificidade, então quem vence é a ordem no CSS GERADO, não a
   * ordem na string do `className`. Passar `tw-rounded-none` por cima de um
   * `tw-rounded-2xl` embutido **não é garantido** — o resultado depende da ordem
   * interna do framework. Em vez de mascarar isso com `!important` em toda
   * sobrescrita, o componente simplesmente não emite o que o consumidor quer
   * trocar. (A alternativa seria a dependência `tailwind-merge`.)
   */
  aparencia?: 'card' | 'nenhuma';
  children: React.ReactNode;
}

const Card: React.FC<PropsCard> = ({
  interativo = false,
  onClick,
  rotuloAcessivel,
  aparencia = 'card',
  className = '',
  children,
  ...resto
}) => {
  const aoTeclar = (evento: React.KeyboardEvent<HTMLDivElement>) => {
    if (!interativo || !onClick) return;
    // 'Spacebar' cobre navegadores antigos que não usam a chave ' '.
    if (evento.key === 'Enter' || evento.key === ' ' || evento.key === 'Spacebar') {
      evento.preventDefault(); // sem isto, Espaço rola a página
      onClick();
    }
  };

  return (
    <div
      className={[
        aparencia === 'card'
          ? `tw-bg-white tw-rounded-2xl tw-border tw-border-superficie-borda ${interativo ? '' : 'tw-shadow-sm'}`
          : '',
        interativo
          ? [
              'tw-cursor-pointer tw-select-none tw-transition-all tw-duration-150',
              'active:tw-translate-y-0 active:tw-scale-[0.99]',
              'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-brand/50 focus-visible:tw-ring-offset-2',
              /* `prefers-reduced-motion` é requisito da spec 01 §7. */
              'motion-reduce:tw-transition-none motion-reduce:hover:tw-translate-y-0',
              /* Elevação e realce de borda só fazem sentido quando há superfície
                 — numa linha de lista o hover vira realce de fundo, definido
                 por quem consome. */
              aparencia === 'card'
                ? 'hover:tw-border-brand/30 hover:tw-shadow-md hover:-tw-translate-y-0.5'
                : 'hover:tw-bg-brand-tint',
            ].join(' ')
          : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      role={interativo ? 'button' : undefined}
      tabIndex={interativo ? 0 : undefined}
      aria-label={interativo ? rotuloAcessivel : undefined}
      onClick={interativo ? onClick : undefined}
      onKeyDown={aoTeclar}
      {...resto}
    >
      {children}
    </div>
  );
};

export default Card;
